import { useState } from "react";

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

function getPageNumbers(current: number, total: number): (number | "...")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages: (number | "...")[] = [1, 2, 3];
  if (current > 5) {
    pages.push("...");
  }
  const start = Math.max(4, current - 1);
  const end = Math.min(total - 3, current + 1);
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  if (end < total - 3) {
    pages.push("...");
  }
  pages.push(total - 2, total - 1, total);
  return pages;
}

export default function Pagination({ page, pageSize, total, onPageChange, onPageSizeChange }: PaginationProps) {
  const [jumpInput, setJumpInput] = useState("");
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = page + 1;

  const handleJump = () => {
    const num = parseInt(jumpInput, 10);
    if (num >= 1 && num <= totalPages) {
      onPageChange(num - 1);
      setJumpInput("");
    }
  };

  const pages = getPageNumbers(current, totalPages);

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
      <div className="flex items-center gap-3">
        <p className="text-xs text-gray-400">共 {total} 条</p>
        <select
          value={pageSize}
          onChange={(e) => { onPageSizeChange(Number(e.target.value)); onPageChange(0); }}
          className="px-2 py-1 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] bg-white cursor-pointer"
        >
          <option value={10}>10条/页</option>
          <option value={20}>20条/页</option>
          <option value={50}>50条/页</option>
        </select>
      </div>
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={page === 0}
          className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-s-line"></i>
        </button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`dots-${i}`} className="px-1 text-xs text-gray-300 select-none">...</span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p - 1)}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                current === p
                  ? "bg-[#1a3a5c] text-white"
                  : "text-gray-600 hover:bg-gray-50 border border-gray-200"
              }`}
            >
              {p}
            </button>
          )
        )}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page + 1 >= totalPages}
          className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-right-s-line"></i>
        </button>
        {totalPages > 7 && (
          <div className="flex items-center gap-1 ml-2">
            <span className="text-[10px] text-gray-400 whitespace-nowrap">跳转</span>
            <input
              type="text"
              value={jumpInput}
              onChange={(e) => setJumpInput(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && handleJump()}
              placeholder="页"
              className="w-10 px-1.5 py-1 rounded border border-gray-200 text-xs text-center text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white"
            />
            <button
              onClick={handleJump}
              className="px-2 py-1 rounded bg-gray-50 text-xs text-gray-600 hover:bg-gray-100 cursor-pointer whitespace-nowrap"
            >
              GO
            </button>
          </div>
        )}
      </div>
    </div>
  );
}